import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import { InfoText, LightDarkText, LightText } from "@/common/ui/Text";
import { Col } from "@/common/css";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import { toastErrorMessage } from "@/utils/helper/myToast";
import axios from "axios";
import { getUploadUrls } from "@/services/common/docs.services";
import Icon from "@/common/ui/Image";
import { useSelector } from "react-redux";
import { loginSelector } from "@/store/features/loginSlice";
import { getCroppedImg } from "@/utils/helper/image";
import LazyImage from "@/common/ui/LazyImage";
import { requestUserConfirmation } from "@/utils/helper/promise";
import { checkAuth } from "@/services/common/user.services";
import { useAppDispatch } from "@/store/store";
import { loginSuccessAction } from "@/store/actions/login.actions";
import { generateAcronym } from "@/utils/helper/string";

interface UploadProfileProps {
  name: string;
  profileUrl?: string;
  handleOnClick: () => void;
}
const PROFILE_KEYS: any = {
  profile_pic_path: "profilePicUrl",
  logo_path: "logoPicUrl",
};

export const UploadProfile: React.FC<UploadProfileProps> = ({ name, profileUrl, handleOnClick }) => {
  const acroNym = generateAcronym(name);

  return (
    <div className="p-1 py-2 md:p-4">
      {profileUrl ? (
        <div className="relative w-fit">
          <LazyImage src={profileUrl} alt="profile-pic" className="size-12 xl:size-14 2xl:size-16 rounded-full p-[1px]" />
          <button
            className="bg-text-primary size-4 lg:size-[18px] xl:size-5 flex items-center justify-center rounded-full absolute -right-1 bottom-1"
            onClick={handleOnClick}
          >
            <Icon src="/icons/edit-icon.svg" alt="edit-icon" className="size-[10px]" />
          </button>
        </div>
      ) : (
        <motion.div
          className={`size-12 xl:size-14 2xl:size-16 rounded-full flex items-center justify-center ${acroNym ? "border-2" : ""}  border-text-primary relative`}
        >
          {acroNym ? (
            <span className="text-text-primary font-bold text-10 md:text-base 2xl:text-lg !leading-normal">{acroNym}</span>
          ) : (
            <Icon src="/icons/img-ph.svg" alt="edit-icon" className="text-text-primary size-12 xl:size-14 2xl:size-16" />
          )}
          <button className="bg-text-primary size-4 lg:size-5 xl:size-[22px] rounded-full absolute -right-1 -bottom-1" onClick={handleOnClick}>
            <PlusIcon className="text-white size-4 lg:size-5 xl:size-[22px]" />
          </button>
        </motion.div>
      )}
    </div>
  );
};

interface UploadProfileModalProps {
  onClose: (props?: any) => void;
  openModal: () => void;
  profileUrl?: string;
  updateAndFetchProfile: (data: any) => Promise<void>;
  profile_key: string;
  isFreeCrop?: boolean;
}

const UploadProfileModal: React.FC<UploadProfileModalProps> = ({ onClose, profileUrl, updateAndFetchProfile, openModal, profile_key }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const imgRef = useRef<any>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { currentUser } = useSelector(loginSelector);
  const [crop, setCrop] = useState<Crop | undefined>(undefined);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      if (validateFile(file)) {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validateFile = (file: File): boolean => {
    const validTypes = ["image/jpeg", "image/png"];
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    if (!(file.size <= maxSize)) {
      setError("The file size should be less than or equal to 5 MB only.");
      return false;
    }
    if (!validTypes.includes(file.type)) {
      setError("The file should be PNG and JPG format.");
    }
    setError("");
    return true;
  };

  const handleBrowseClick = () => {
    if (selectedFile) return handleUpload();
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    let croppedFile: any;
    if (selectedFile && imgRef.current) {
      try {
        setIsLoading(true);
        croppedFile = await getCroppedImg(imgRef.current, crop as PixelCrop, selectedFile.name);
        setSelectedFile(croppedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          // setPreviewUrl(reader.result as string);
          // console.log({ url: reader.result as string });
        };
        reader.readAsDataURL(croppedFile);
      } catch (error) {
        setIsLoading(false);
        console.error("Error cropping image:", error);
      }
    }

    try {
      //Get pre-signed URL
      const response: any = await getUploadUrls({
        fileName: croppedFile.name,
        fileType: croppedFile.type,
        uploadKey: "profile",
      });
      const { preSignedUrl, objectKey } = response.data?.data || {};

      // Upload file to the pre-signed URL
      await axios.put(preSignedUrl, croppedFile, {
        headers: {
          "Content-Type": croppedFile.type,
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log("uploading===>", percentCompleted, "%");
        },
      });
      const payload: any = { signupType: currentUser?.userType };
      payload[PROFILE_KEYS[profile_key]] = objectKey;
      await updateAndFetchProfile(payload);
    } catch (error) {
      console.error("Error uploading file:", error);
      toastErrorMessage("Failed to upload file.");
    } finally {
      setIsLoading(false);
      onClose();
      const res: any = await checkAuth();
      if (res.status === 200) {
        const { data } = res.data;
        dispatch(loginSuccessAction(data));
      }
    }
  };

  const handleCrop = (c: any) => {
    if (imgRef.current) {
      const imgWidth = imgRef.current.width;
      const imgHeight = imgRef.current.height;
      // For images >= 150x150, restrict crop to 150x150
      if (imgWidth >= 150 && imgHeight >= 150) {
        let height: any, width: any;

        if (c.height >= 150) {
          height = c.height;
        }
        if (c.width >= 150) {
          width = c.width;
        }
        setCrop((prev) => {
          return {
            ...c,
            width: width ? width : prev?.width,
            height: height ? height : prev?.height,
          };
        });
      } else {
        // For smaller images, crop to full image
        setCrop({
          unit: "px",
          width: imgWidth,
          height: imgHeight,
          x: 0,
          y: 0,
        });
      }
    }
  };

  const handleRemove = async () => {
    if (!previewUrl) {
      if (profileUrl) {
        try {
          await requestUserConfirmation({ openModal, closeModal: onClose });
          const payload: any = { signupType: currentUser?.userType };
          payload[PROFILE_KEYS[profile_key]] = "";
          await updateAndFetchProfile(payload);
          onClose();
          const res: any = await checkAuth();
          if (res.status === 200) {
            const { data } = res.data;
            dispatch(loginSuccessAction(data));
          }
        } catch (err) {
          console.log({ err });
          return;
        }
      }
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const centerCrop = useCallback((mediaWidth: number, mediaHeight: number) => {
    // If image is smaller than 150px in either dimension, crop to full image'
    if (mediaWidth < 150 || mediaHeight < 150) {
      setCrop({
        unit: "px",
        width: mediaWidth > 10 ? mediaWidth : 10,
        height: mediaHeight > 10 ? mediaHeight : 10,
        x: 0,
        y: 0,
      });
    } else {
      const x = (mediaWidth - 150) / 2;
      const y = (mediaHeight - 150) / 2;
      setCrop({
        unit: "px",
        width: 150,
        height: 150,
        x: x,
        y: y,
      });
    }
  }, []);

  const onImageLoad = useCallback(() => {
    if (imgRef.current) {
      const width = imgRef.current.width;
      const height = imgRef.current.height;
      // If image is small, set crop to full image
      if (width < 150 || height < 150) {
        centerCrop(width, height);
      } else {
        // Set crop to default 150x150 centered
        const x = (width - 150) / 2;
        const y = (height - 150) / 2;
        setCrop({
          unit: "px",
          width: 150,
          height: 150,
          x,
          y,
        });
      }
    }
  }, [centerCrop]);
  // console.log({ crop });
  return (
    <div className="p-4">
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/jpeg,image/png" className="hidden" />
      {error && <InfoText text={error} className=" my-4" />}
      <div
        className="border-text-primary border rounded-llg border-dashed min-h-40 bg-brand-light max-h-[calc(100vh-100px)] overflow-y-auto"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {previewUrl || profileUrl ? (
          <div className="flex flex-col gap-4 m-8 justify-center items-center">
            {selectedFile && <LightDarkText text="To crop, drag the highlighted area" className=" !text-text-default" />}{" "}
            {previewUrl ? (
              <ReactCrop crop={selectedFile ? crop : undefined} onChange={handleCrop} className="rounded" keepSelection={false}>
                {previewUrl && <img src={previewUrl} ref={imgRef} alt="Preview" className="rounded" onLoad={onImageLoad} />}
              </ReactCrop>
            ) : (
              <>{profileUrl && <LazyImage src={profileUrl} alt="Preview" className="rounded" />}</>
            )}
            <div>
              {!profileUrl && (
                <Button variant="link" className="text-13 text-text-primary !p-1 " onClick={onImageLoad}>
                  Reset crop area
                </Button>
              )}
            </div>
            <div className="flex gap-2 justify-center py-4 sticky bottom-0 bg-brand-light w-full">
              <Button onClick={handleRemove} variant="outline" className=" !text-text-default !text-13" disabled={isLoading}>
                Remove
              </Button>
              {!profileUrl ? (
                <Button onClick={handleUpload} disabled={isLoading}>
                  {isLoading ? "Uploading..." : "Upload"}
                </Button>
              ) : (
                <Button disabled={isLoading} onClick={handleBrowseClick}>
                  {selectedFile ? (isLoading ? "Uploading..." : "Upload") : "Browse attachments"}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <Col className="py-4 px-25 gap-y-2 items-center">
            <LightDarkText text="Upload your file" className="pb-2 border-b-[1px] border-text-light3" />
            <LightDarkText text="Drag and drop your attachments here" />
            <LightText text="Your file needs to be in JPEG, PNG format with a size of 5MB." className="!text-10" />
            <Button onClick={handleBrowseClick}>Browse attachments</Button>
          </Col>
        )}
      </div>
    </div>
  );
};

export default UploadProfileModal;
